import {
  AvailableInterval,
  GroupBookingInterval,
  TimeSlot,
} from "../AppDataProvider/types";
import {
  GetOrganisationsAvailabilityDocument,
  GetOrganisationsAvailabilityQuery,
  GetOrganisationsAvailabilityQueryVariables,
} from "../GeneratedGraphQL";
import {
  organisationWithMapAvailabilityBookingTool,
  twoOrganisationBookingTool,
} from "./bookingTool";
import {
  testOrganisation12,
  testOrganisation2,
  testOrganisationWithFeatureFlag,
} from "./organisation";

import { defaultResource } from "./resource";
import { MockedResponse } from "@apollo/client/testing";
/**
 * Mock intervals for the 1st day of the month
 */
const firstDayRange = {
  end: new Date("2124-01-02T00:00:00").toISOString(),
  start: new Date("2124-01-01T00:00:00").toISOString(),
};

export const defaultSingleOrganisationAvailabilityRequestVariables: GetOrganisationsAvailabilityQueryVariables =
  {
    id: organisationWithMapAvailabilityBookingTool._id,
    meta: {
      sessionId: "12345678",
    },
    organisationIds: [testOrganisationWithFeatureFlag._id],
    queryByOrganisationTimeZone: true,
    range: firstDayRange,
    timezone: undefined,
  };

export const defaultMultipleOrganisationsAvailabilityRequestVariables: GetOrganisationsAvailabilityQueryVariables =
  {
    id: twoOrganisationBookingTool._id,
    meta: {
      sessionId: "12345678",
    },
    organisationIds: [
      testOrganisation2._id,
      testOrganisationWithFeatureFlag._id,
    ],
    queryByOrganisationTimeZone: true,
    range: firstDayRange,
    timezone: undefined,
  };

export const defaultMultipleOrganisationsAvailabilityTimezonesRequestVariables: GetOrganisationsAvailabilityQueryVariables =
  {
    id: twoOrganisationBookingTool._id,
    meta: {
      sessionId: "12345678",
    },
    organisationIds: [
      testOrganisation12._id,
      testOrganisationWithFeatureFlag._id,
    ],
    queryByOrganisationTimeZone: true,
    range: firstDayRange,
    timezone: undefined,
  };

export const defaultOrganisationsAvailabilityIntervals: (
  | AvailableInterval
  | GroupBookingInterval
)[] = [
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T09:30:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T09:00:00.000Z",
  },
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T10:00:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T09:30:00.000Z",
  },
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T10:30:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T10:00:00.000Z",
  },
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T11:00:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T10:30:00.000Z",
  },
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T11:30:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T11:00:00.000Z",
  },
  {
    __typename: "AvailableInterval",
    end: "2124-01-01T12:00:00.000Z",
    resourceIds: [defaultResource._id],
    start: "2124-01-01T11:30:00.000Z",
  },
];

export const defaultSingleOrganisationAvailabilityIntervalsRequest: MockedResponse<GetOrganisationsAvailabilityQuery> =
  {
    request: {
      query: GetOrganisationsAvailabilityDocument,
      variables: defaultSingleOrganisationAvailabilityRequestVariables,
    },
    result: {
      data: {
        bookingTool: {
          _id: organisationWithMapAvailabilityBookingTool._id,
          name: "Test Booking Tool, single org & service",
          organisationsAvailability: [
            {
              intervals: defaultOrganisationsAvailabilityIntervals,
              organisation: {
                _id: "111222333444555666777666",
                defaultTimeZone: "Europe/London",
                name: "Test Org A",
              },
            },
          ],
        },
      },
    },
  };

export const defaultMultipleOrganisationAvailabilityIntervalsRequest: MockedResponse<GetOrganisationsAvailabilityQuery> =
  {
    request: {
      query: GetOrganisationsAvailabilityDocument,
      variables: defaultMultipleOrganisationsAvailabilityRequestVariables,
    },
    result: {
      data: {
        bookingTool: {
          _id: twoOrganisationBookingTool._id,
          name: "Test Booking Tool",
          organisationsAvailability: [
            {
              intervals: defaultOrganisationsAvailabilityIntervals,
              organisation: {
                _id: "111222333444555666777667",
                defaultTimeZone: "Europe/London",
                name: "Test Org B",
              },
            },
            {
              intervals: defaultOrganisationsAvailabilityIntervals,
              organisation: {
                _id: "111222333444555666777666",
                defaultTimeZone: "Europe/London",
                name: "Test Org A",
              },
            },
          ],
        },
      },
    },
  };

export const defaultMultipleOrganisationAvailabilityIntervalsDifferentTimezoneRequest: MockedResponse<GetOrganisationsAvailabilityQuery> =
  {
    request: {
      query: GetOrganisationsAvailabilityDocument,
      variables:
        defaultMultipleOrganisationsAvailabilityTimezonesRequestVariables,
    },
    result: {
      data: {
        bookingTool: {
          _id: twoOrganisationBookingTool._id,
          name: "Test Booking Tool",
          organisationsAvailability: [
            {
              intervals: defaultOrganisationsAvailabilityIntervals,
              organisation: {
                _id: "11122233344455566677767522",
                defaultTimeZone: "America/Los_Angeles",
                name: "Test Org L",
              },
            },
            {
              intervals: defaultOrganisationsAvailabilityIntervals,
              organisation: {
                _id: "111222333444555666777666",
                defaultTimeZone: "Europe/London",
                name: "Test Org A",
              },
            },
          ],
        },
      },
    },
  };

export const noOrganisationsAvailabilityIntervalsRequest: MockedResponse<GetOrganisationsAvailabilityQuery> =
  {
    request: {
      query: GetOrganisationsAvailabilityDocument,
      variables: defaultSingleOrganisationAvailabilityRequestVariables,
    },
    result: {
      data: {
        bookingTool: {
          _id: twoOrganisationBookingTool._id,
          name: "Test Booking Tool, single org & service",
          organisationsAvailability: [
            {
              intervals: [],
              organisation: {
                _id: "111222333444555666777666",
                defaultTimeZone: "Europe/London",
                name: "Test Org A",
              },
            },
          ],
        },
      },
    },
  };

export const defaultOrganisationsTimeSlots: TimeSlot[] = [
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T09:30:00").toISOString(),
      resourceIds: [defaultResource._id],
      start: new Date("2124-01-01T09:00:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T10:00:00").toISOString(),
      resourceIds: [defaultResource._id],
      start: new Date("2124-01-01T09:30:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T10:30:00").toISOString(),
      resourceIds: [defaultResource._id],
      start: new Date("2124-01-01T10:00:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T11:00:00").toISOString(),
      resourceIds: [defaultResource._id],
      start: new Date("2124-01-01T10:30:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T11:30:00").toISOString(),
      resourceIds: [defaultResource._id],
      start: new Date("2124-01-01T11:00:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T12:00:00").toISOString(),
      resourceIds: [defaultResource._id],
      start: new Date("2124-01-01T11:30:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T12:30:00").toISOString(),
      resourceIds: [defaultResource._id],
      start: new Date("2124-01-01T12:00:00").toISOString(),
    },
  },
  {
    interval: {
      __typename: "AvailableInterval",
      end: new Date("2124-01-01T13:00:00").toISOString(),
      resourceIds: [defaultResource._id],
      start: new Date("2124-01-01T12:30:00").toISOString(),
    },
  },
];
